import React, {useMemo, useState, useEffect, useCallback} from 'react';
import dayjs from 'dayjs';
import {Button, Table, message, Tabs} from "antd";
import type { TableColumnsType } from 'antd';
import ShipToHereTaskFilter, {DEFAULT_FILTER_OPTIONS, FilterOptions} from "./components/ShipToHereTaskFilter";
import {TmsRouteTypeEnum, TmsBusinessTypeEnum, TmsTaskStatusEnum, TmsTaskTypeEnum, TmsVehicleStatus} from "../../../enums/TmsEnum";
import {useMutation, useQuery} from "@apollo/client";
import {GET_TMS_CARRIERS_BY_WAREHOUSE_ID, GetTmsCarriersByWarehouseIdResponse} from "../schedule/graphql/queries/getTmsCarriers";
import {GET_TMS_DRIVERS_BY_WAREHOUSE_ID, GetTmsDriversByWarehouseIdResponse} from "../account/graphql/queries/getAllTmsDrivers";
import {
    GET_TMS_VEHICLES_BY_WAREHOUSE_ID_FOR_SELECT,
    GetTmsVehiclesByWarehouseIdForSelectResponse
} from "../schedule/graphql/queries/getTmsVehiclesWithModel";
import {TmsTaskFiltersInput} from "./graphql/queries/getTmsTasks";
import {GET_SHIP_TO_HERE_TASK_LIST, ShipToHereTaskListResponse, ShipToHereTaskType} from "./graphql/queries/getShipToHereTaskList";
import EditTaskDrawer from "./components/EditTaskDrawer";
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {GENERATE_TMS_TASK_DOWNLOAD_REPORT, GenerateTmsTaskDownloadReportResponse} from "../download/graphql/mutations/generateTmsTaskDownloadReport";
import { useNavigate } from 'react-router-dom';
import { t } from '../../../i18n';
import BolPreviewModalV3, { BolDataV3 } from "./components/bol/v3/BolPreviewModal";
import { TaskGroupType } from "./components/bol/v3/types/bolTypes";
import { useBolGenerationV3 } from './hooks/useBolGenerationV3';
import {useTaskCommon} from './hooks/useTaskCommon';
import {useTaskRouteRowSpan} from './hooks/useTaskRouteRowSpan';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getCurrentTimeInTimezone } from '../../../utils/timezoneUtils';

// 任务日期默认区间：结束为当天，向前共 3 天（含当天）
// 任务日期是站点当地日期，优先按 Hub 时区（timezoneLocation，IANA）取「今天」，取不到时退回浏览器本地时间
const getDefaultTaskDate = (timezoneLocation?: string): [string, string] => {
    const now = timezoneLocation ? getCurrentTimeInTimezone(timezoneLocation) : dayjs();
    return [now.subtract(2, 'days').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
};

const ShipToHereTask: React.FC = () => {
    const navigate = useNavigate();
    const { currentWarehouse } = useWarehouseContext();

    // 计算初始 filters，任务日期默认最近 3 天（结束为当天）
    const initialFilters = useMemo(() => {
        return {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: String(TmsRouteTypeEnum.Linehaul),
            taskDate: getDefaultTaskDate(currentWarehouse?.timezoneLocation)
        };
    }, [currentWarehouse?.timezoneLocation]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // 初始化时默认选中干线（routeType = 1）和默认任务日期
    const [filters, setFilters] = useState<FilterOptions>(initialFilters);
    const [tempFilters, setTempFilters] = useState<FilterOptions>(initialFilters);
    const [filterResetSignal, setFilterResetSignal] = useState(0);
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [bolModalOpen, setBolModalOpen] = useState(false);
    const [bolData, setBolData] = useState<BolDataV3 | null>(null);
    const [selectedTaskForBOL, setSelectedTaskForBOL] = useState<any>(null);
    const { data: carriersData } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });
    const { data: driversData } = useQuery<GetTmsDriversByWarehouseIdResponse>(GET_TMS_DRIVERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null,
            routeType: filters.routeType ? Number(filters.routeType) : null
        },
        skip: !currentWarehouse?.id
    });
    const { data: vehiclesData } = useQuery<GetTmsVehiclesByWarehouseIdForSelectResponse>(GET_TMS_VEHICLES_BY_WAREHOUSE_ID_FOR_SELECT, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null,
            status: TmsVehicleStatus.AVAILABLE,
            routeType: filters.routeType ? Number(filters.routeType) : null
        },
        skip: !currentWarehouse?.id
    });
    const [generateTmsTaskDownloadReport, { loading: downloadReportLoading }] = useMutation<GenerateTmsTaskDownloadReportResponse>(GENERATE_TMS_TASK_DOWNLOAD_REPORT);

    const taskOptions = useMemo(() => [
        { label: String(t('tms.task.taskTypes.regular')), value: TmsTaskTypeEnum.Regular },
        { label: String(t('tms.task.taskTypes.temporary')), value: TmsTaskTypeEnum.Temporary },
        { label: String(t('tms.task.taskTypes.thirdParty')), value: TmsTaskTypeEnum.ThirdParty }
    ], [t]);

    const businessTypeOptions = useMemo(() => [
        { label: String(t('tms.task.businessTypes.ownRoute')), value: TmsBusinessTypeEnum.OwnRoute },
        { label: String(t('tms.task.businessTypes.temuGA')), value: TmsBusinessTypeEnum.TemuGA },
        { label: String(t('tms.task.businessTypes.temuSCF')), value: TmsBusinessTypeEnum.TemuSCF },
        { label: String(t('tms.task.businessTypes.tiktok')), value: TmsBusinessTypeEnum.TikTok },
    ], [t]);

    const routeTypeOptions = useMemo(() => [
        { label: String(t('tms.route.routeTypes.linehaul')), value: TmsRouteTypeEnum.Linehaul },
        { label: String(t('tms.route.routeTypes.feeder')), value: TmsRouteTypeEnum.Feeder }
    ], [t]);

    // 根据当前 filters.routeType 计算 Tab 激活项
    const activeRouteTypeTab = useMemo(() => {
        if (filters.routeType === String(TmsRouteTypeEnum.Linehaul)) {
            return 'linehaul';
        }
        if (filters.routeType === String(TmsRouteTypeEnum.Feeder)) {
            return 'feeder';
        }
        // 默认展示干线
        return 'linehaul';
    }, [filters.routeType]);

    const handleRouteTypeTabChange = (key: string) => {
        let routeTypeValue: string;
        if (key === 'linehaul') {
            routeTypeValue = String(TmsRouteTypeEnum.Linehaul);
        } else {
            routeTypeValue = String(TmsRouteTypeEnum.Feeder);
        }

        // 切换 tab 时清空路线选项、班次选项和承运商选项，然后根据条件进行查询
        const nextFilters: FilterOptions = {
            ...filters,
            routeType: routeTypeValue,
            routeId: '', // 清空路线选项
            scheduleId: '', // 清空班次选项
            carrierId: '' // 清空承运商选项
        };
        setTempFilters(nextFilters);
        setFilters(nextFilters);
        setCurrentPage(1);
    };

    const taskStatusOptions = useMemo(() => [
        { label: String(t('tms.task.status.published')), value: TmsTaskStatusEnum.Published },
        { label: String(t('tms.task.status.inProgress')), value: TmsTaskStatusEnum.InProgress },
        { label: String(t('tms.task.status.completed')), value: TmsTaskStatusEnum.Completed },
        { label: String(t('tms.task.status.abnormal')), value: TmsTaskStatusEnum.Abnormal },
        { label: String(t('tms.task.status.cancelled')), value: TmsTaskStatusEnum.Cancelled }
    ], [t]);



    const driverOptions = useMemo(() => {
        if (!driversData?.getTmsDriversByWarehouseId) return [];
        return driversData.getTmsDriversByWarehouseId.map(driver => ({
            label: driver.driverName,
            value: driver.id
        }));
    }, [driversData]);

    const vehicleOptions = useMemo(() => {
        if (!vehiclesData?.getTmsVehiclesByWarehouseIdForSelect) return [];
        return vehiclesData.getTmsVehiclesByWarehouseIdForSelect.map(vehicle => ({
            label: vehicle.licensePlate,
            value: vehicle.id.toString()
        }));
    }, [vehiclesData]);

    // 当 Hub 改变时，重置分页到第一页，并按新 Hub 时区重置默认任务日期
    useEffect(() => {
        if (currentWarehouse?.id) {
            setCurrentPage(1);
            const defaultTaskDate = getDefaultTaskDate(currentWarehouse?.timezoneLocation);
            setFilters(prev => ({ ...prev, taskDate: defaultTaskDate }));
            setTempFilters(prev => ({ ...prev, taskDate: defaultTaskDate }));
        }
    }, [currentWarehouse?.id, currentWarehouse?.timezoneLocation]);

    // 构建查询过滤条件（已点击「筛选」后生效，用于列表查询）
    const queryFilters: TmsTaskFiltersInput = useMemo(() => {
        return {
            taskType: filters.taskType ? Number(filters.taskType) : undefined,
            businessType: filters.businessType ? Number(filters.businessType) : undefined,
            routeId: filters.routeId ? Number(filters.routeId) : undefined,
            routeCode: filters.routeCode || undefined,
            taskNumber: filters.taskNumber || undefined,
            taskStatuses: filters.taskStatus.length > 0 ? filters.taskStatus.map(s => Number(s)) : undefined,
            routeType: filters.routeType ? Number(filters.routeType) : undefined,
            scheduleId: filters.scheduleId ? Number(filters.scheduleId) : undefined,
            carrierId: filters.carrierId ? Number(filters.carrierId) : undefined,
            driverId: filters.driverId ? Number(filters.driverId) : undefined,
            vehicleId: filters.vehicleId ? Number(filters.vehicleId) : undefined,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
            taskDateStart: filters.taskDate?.[0] || undefined,
            taskDateEnd: filters.taskDate?.[1] || undefined,
        };
    }, [filters, currentWarehouse?.id]);

    // 下载时使用当前表单的筛选条件（与是否点击「筛选」无关），保证下载数据与用户勾选条件一致
    const downloadFilters: TmsTaskFiltersInput = useMemo(() => {
        return {
            taskType: tempFilters.taskType ? Number(tempFilters.taskType) : undefined,
            businessType: tempFilters.businessType ? Number(tempFilters.businessType) : undefined,
            routeId: tempFilters.routeId ? Number(tempFilters.routeId) : undefined,
            routeCode: tempFilters.routeCode || undefined,
            taskNumber: tempFilters.taskNumber || undefined,
            taskStatuses: tempFilters.taskStatus?.length ? tempFilters.taskStatus.map(s => Number(s)) : undefined,
            routeType: tempFilters.routeType ? Number(tempFilters.routeType) : undefined,
            scheduleId: tempFilters.scheduleId ? Number(tempFilters.scheduleId) : undefined,
            carrierId: tempFilters.carrierId ? Number(tempFilters.carrierId) : undefined,
            driverId: tempFilters.driverId ? Number(tempFilters.driverId) : undefined,
            vehicleId: tempFilters.vehicleId ? Number(tempFilters.vehicleId) : undefined,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
            taskDateStart: tempFilters.taskDate?.[0] ?? undefined,
            taskDateEnd: tempFilters.taskDate?.[1] ?? undefined,
        };
    }, [tempFilters, currentWarehouse?.id]);

    // Query for task listing
    const { data, loading, refetch } = useQuery<ShipToHereTaskListResponse>(GET_SHIP_TO_HERE_TASK_LIST, {
        variables: {
            filter: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            }
        },
        skip: !currentWarehouse?.id,
        fetchPolicy: 'no-cache'
    });

    const taskData = data?.getShipToHereTaskList?.content || [];
    const totalElements = data?.getShipToHereTaskList?.totalElements || 0;

    // 使用任务通用逻辑 hook
    const { generateBolData, getTaskStatusClassName, getWaypointStatusClassName, getWaypointStatusText } = useTaskCommon();
    const { handleBolPrint: handleBolPrintV3 } = useBolGenerationV3();
    const { routeRowSpanMap, taskDateRowSpanMap } = useTaskRouteRowSpan(taskData);


    // 根据任务状态值，返回文本信息
    const getTaskStatusText = (status: number) => {
        return taskStatusOptions.find(option => option.value === status)?.label || String(t('tms.task.status.unknown'))
    }

    const columns: TableColumnsType<any> = useMemo(() => [
        {
            title: String(t('tms.task.columns.route')),
            dataIndex: 'routeName',
            key: 'routeName',
            align: 'center',
            width: 160,
            fixed: 'left',
            render: (routeName: string | null | undefined, record: any) => {
                if (!routeName || !record.routeId) return '-';
                return (
                    <span className="whitespace-normal" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {routeName}
                        {record.routeCode && <span>（{record.routeCode}）</span>}
                    </span>
                );
            },
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = routeRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: String(t('tms.task.columns.businessType')),
            dataIndex: 'businessType',
            key: 'businessType',
            align: 'center',
            width: 140,
            fixed: 'left',
            render: (value: number) => {
                const option = businessTypeOptions.find(option => option.value === value);
                return option?.label || '-';
            }
        },
        {
            title: String(t('tms.task.columns.taskNumber')),
            dataIndex: 'taskNumber',
            key: 'taskNumber',
            align: 'center',
            width: 160,
            fixed: 'left',
            render: (taskNumber: string, record: any) => {
                return (
                    <Button
                        type="link"
                        onClick={() => {
                            setSelectedTaskId(record.id);
                            setTaskDrawerOpen(true);
                        }}
                        className="p-0 h-auto whitespace-normal"
                        style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {taskNumber}
                    </Button>
                );
            },
        },
        {
            title: String(t('tms.task.columns.taskDate')),
            dataIndex: 'taskDate',
            key: 'taskDate',
            align: 'center',
            width: 120,
            fixed: 'left',
            render: (date: string) => date || '-',
            onCell: (_: any, index?: number) => {
                if (index === undefined) return {};
                const rowSpanInfo = taskDateRowSpanMap.get(index);
                if (rowSpanInfo) {
                    return {
                        rowSpan: rowSpanInfo.rowSpan,
                    };
                }
                return { rowSpan: 1 };
            },
        },
        {
            title: String(t('tms.task.columns.taskType')),
            dataIndex: 'taskType',
            key: 'taskType',
            align: 'center',
            width: 120,
            render: (type: number) => {
                const typeOption = taskOptions.find(option => option.value === type);
                return typeOption?.label || '-';
            }
        },
        {
            title: String(t('tms.task.columns.planDepartureTime')),
            dataIndex: 'planDepartureAt',
            key: 'planDepartureTime',
            align: 'center',
            width: 180,
            render: (_: unknown, record: any) => {
                const { planDepartureDate, planDepartureTime, planDepartureTimezone } = record;
                const parts = [planDepartureDate, planDepartureTime, planDepartureTimezone].filter(Boolean);
                return parts.length > 0 ? parts.join(' ') : '-';
            },
        },
        {
            title: String(t('tms.task.columns.vehicleEstimatedArrivalTime')),
            dataIndex: 'vehicleEstimatedArrivalAt',
            key: 'vehicleEstimatedArrivalTime',
            align: 'center',
            width: 180,
            render: (_: unknown, record: any) => {
                const { vehicleEstimatedArrivalDate, vehicleEstimatedArrivalTime, vehicleEstimatedArrivalTimezone } = record;
                const parts = [vehicleEstimatedArrivalDate, vehicleEstimatedArrivalTime, vehicleEstimatedArrivalTimezone].filter(Boolean);
                return parts.length > 0 ? parts.join(' ') : '-';
            },
        },
        {
            title: String(t('tms.task.columns.currentStation')),
            dataIndex: 'currentStation',
            key: 'currentStation',
            align: 'center',
            width: 120,
            render: (currentStation: string | null | undefined) => currentStation || '-',
        },
        {
            title: String(t('tms.task.columns.taskStatus')),
            dataIndex: 'taskStatus',
            key: 'taskStatus',
            align: 'center',
            width: 120,
            render: (status: number, record: any) => {
                let statusText: string;
                let className: string;
                switch(status) {
                    case TmsTaskStatusEnum.Draft:
                    case TmsTaskStatusEnum.Completed:
                    case TmsTaskStatusEnum.Cancelled:
                    case TmsTaskStatusEnum.Abnormal:
                        className = getTaskStatusClassName(status);
                        statusText = getTaskStatusText(status);
                        break;

                    case TmsTaskStatusEnum.Published:
                    case TmsTaskStatusEnum.InProgress:
                        // 获取途经点状态
                        const waypointStatus = record.currentWaypointStatus;
                        className = getWaypointStatusClassName(waypointStatus);
                        statusText = getWaypointStatusText(waypointStatus);
                        break;

                    default:
                        className = getTaskStatusClassName(status);
                        statusText = getTaskStatusText(status);
                }

                return (
                    <Button
                        size="small"
                        className={`${className} cursor-default hover:opacity-80 border`}
                        style={{ pointerEvents: 'none' }}
                    >
                        {statusText}
                    </Button>
                );
            }
        },
        {
            title: String(t('tms.task.columns.carrier')),
            dataIndex: 'carrierName',
            key: 'carrierName',
            align: 'center',
            width: 140,
            render: (carrierName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {carrierName || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.vehicleModel')),
            dataIndex: 'vehicleTypeName',
            key: 'vehicleTypeName',
            width: 150,
            align: 'center',
            render: (vehicleTypeName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {vehicleTypeName || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.escortCount')),
            dataIndex: 'escortCount',
            key: 'escortCount',
            width: 120,
            align: 'center',
            render: (escortCount: number) => escortCount || '-',
        },
        {
            title: String(t('tms.task.columns.driver')),
            dataIndex: 'driverName',
            key: 'driverName',
            align: 'center',
            width: 120,
            render: (driverName: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {driverName || '-'}
                </span>
            )
        },
        {
            title: String(t('tms.task.columns.gpsTracking')),
            dataIndex: 'gpsTracking',
            key: 'gpsTracking',
            width: 160,
            align: 'center',
            render: (gpsTracking: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {gpsTracking || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.bolNumber')),
            dataIndex: 'bolNumber',
            key: 'bolNumber',
            align: 'center',
            width: 180,
            render: (bolNumber: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {bolNumber || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.vehicle')),
            dataIndex: 'licensePlate',
            key: 'licensePlate',
            align: 'center',
            width: 120,
            render: (licensePlate: string) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {licensePlate || '-'}
                </span>
            ),
        },
        {
            title: String(t('tms.task.columns.action')),
            key: 'action',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, record: any) => {
                // 到达此处列表后端固定排除草稿，这里只有查看与打印 BOL，无编辑/删除
                return (
                    <div className="flex gap-1 justify-center flex-nowrap">
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                setSelectedTaskId(record.id);
                                setTaskDrawerOpen(true);
                            }}
                        >
                            {String(t('tms.task.buttons.view'))}
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                handleBOLPrintWithModal(record);
                            }}
                        >
                            {String(t('tms.task.buttons.printBOL'))}
                        </Button>
                    </div>
                );
            }
        }
    ], [taskOptions, businessTypeOptions, taskStatusOptions, routeTypeOptions, currentWarehouse, routeRowSpanMap, taskDateRowSpanMap, t, getTaskStatusClassName, getWaypointStatusText, getWaypointStatusClassName]);

    const handleFilter = () => {
        setFilters({ ...tempFilters });
        setCurrentPage(1);
    };

    const handleDownloadReport = async () => {
        try {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await generateTmsTaskDownloadReport({
                variables: {
                    filters: downloadFilters,
                    taskListType: 'SHIP_TO_HERE',
                    timezone: userTimezone,
                },
            });
            const reportId = res.data?.generateTmsTaskDownloadReport;
            if (reportId) {
                message.success(t('tms.task.download.submitted') || '报表已提交，请到「下载」页查看并下载');
                if (hasResource([ResourceCode.menu.TMS_DOWNLOAD])) {
                    navigate('/tms/download');
                }
            }
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : '';
            message.error(msg || (t('tms.task.download.failed') || '提交下载失败'));
        }
    };

    const handleResetFilters = () => {
        const resetFiltersValue: FilterOptions = {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: filters.routeType || String(TmsRouteTypeEnum.Linehaul),
            taskDate: getDefaultTaskDate(currentWarehouse?.timezoneLocation)
        };
        setFilterResetSignal(s => s + 1);
        setTempFilters(resetFiltersValue);
        setFilters(resetFiltersValue);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const handleBOLPrintWithModal = useCallback(
        (record: ShipToHereTaskType) => {
            generateBolData(record, handleBolPrintV3, setBolData, setSelectedTaskForBOL, setBolModalOpen);
        },
        [generateBolData, handleBolPrintV3]
    );

    // @ts-ignore
    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter Area */}
                <ShipToHereTaskFilter
                    taskOptions={taskOptions}
                    statusOptions={taskStatusOptions}
                    routeTypeOptions={routeTypeOptions}
                    businessTypeOptions={businessTypeOptions}
                    carriersData={carriersData?.getTmsCarriersByWarehouseId}
                    driverOptions={driverOptions}
                    vehicleOptions={vehicleOptions}
                    initialFilters={initialFilters}
                    currentRouteType={filters.routeType}
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    resetSignal={filterResetSignal}
                />

                {/* Action Buttons + Tabs */}
                <div className="flex justify-between items-center mb-3 pl-0">
                    <div>
                        <Tabs
                            activeKey={activeRouteTypeTab}
                            onChange={handleRouteTypeTabChange}
                            items={[
                                { key: 'linehaul', label: t('tms.route.routeTypes.linehaul') },
                                { key: 'feeder', label: t('tms.route.routeTypes.feeder') },
                            ]}
                            className="[&_.ant-tabs-tab]:font-bold"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={handleDownloadReport}
                            loading={downloadReportLoading}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.menu.download'))}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.filters.filter'))}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{String(t('tms.task.filters.reset'))}</span>
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={taskData}
                        bordered
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        size="middle"
                        tableLayout="auto"
                        className="mb-4"
                    />
                </div>

                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => handlePageChange(page, size)}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* Task Drawer (Edit/View) */}
                <EditTaskDrawer
                    open={taskDrawerOpen}
                    onClose={() => {
                        setTaskDrawerOpen(false);
                        setSelectedTaskId(null);
                        // drawer 内的到站/发车/装卸车等操作只刷新了任务详情，关闭时统一刷新列表，避免按钮按旧状态展示
                        refetch();
                    }}
                    taskId={selectedTaskId}
                    mode="view"
                />

                {/* BOL Preview Modal */}
                <BolPreviewModalV3
                    open={bolModalOpen}
                    onClose={() => {
                        setBolModalOpen(false);
                        setSelectedTaskForBOL(null);
                    }}
                    initialBOLData={bolData}
                    taskGroupType={TaskGroupType.Arrival}
                />

            </div>
    );
};

export default ShipToHereTask;

